import { defineMessage } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import type IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import type { UserRef } from "@mapmycustomers/shared/types/User";
import type {
  CombinedCondition,
  FilterModel,
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import i18nService from "@app/config/I18nService";
import UsersFilter, { USERS_FILTER_OPERATORS } from "@app/util/filters/UsersFilter";
import { csvUserFullNameFormatter } from "@app/util/formatters";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import Field, { FieldProperties } from "./Field";

const incompatibleFilterMessage = defineMessage({
  id: "userField.incompatibleFilter",
  defaultMessage: "Cannot filter by {field} when {incompatibleField} filter is applied",
  description:
    "Tooltip shown when user field filter is disabled because there's some incompatible filter",
});

export const userFormatter = (entity: unknown, user: unknown) =>
  user ? userDisplayName(user as UserRef) : "";

class UserField extends Field {
  constructor(data: Omit<FieldProperties, "type">) {
    super({
      csvExportProperties: { valueFormatter: csvUserFullNameFormatter },
      customFilterConfig: {
        defaultInstance: "user",
        filterInstances: {
          user: UsersFilter,
        },
        operators: [...USERS_FILTER_OPERATORS],
      },
      customGridProperties: {
        cellRenderer: "userCellRenderer",
        // disable tooltips for this field, since we use custom renderer with a popover
        tooltipValueGetter: undefined,
      },
      features: [FieldFeature.FILTERABLE, FieldFeature.SUPPORTS_SET_FILTER],
      filterType: FieldType.ID,
      type: FieldType.OBJECT,
      valueFormatter: userFormatter,
      ...data,
    });
  }

  isFilterEnabled(
    currentFilters: FilterModel,
    currentFilterGroup: CombinedCondition,
    fieldModel: IFieldModel
  ) {
    if (!this.hasFeature(FieldFeature.OWNER_FIELD)) {
      return true;
    }

    const incompatibleField = currentFilterGroup.conditions
      .filter(isSimpleCondition)
      .map((condition) =>
        fieldModel.getByFilterName((condition as SimpleCondition).fieldFilterName)
      )
      .find((field) => field?.hasFeature(FieldFeature.TEAM_FIELD));

    if (!incompatibleField) {
      return true;
    }

    return i18nService.formatMessage(
      incompatibleFilterMessage,
      `Cannot filter by ${this.displayName} when ${incompatibleField.displayName} filter is applied`,
      {
        field: this.displayName,
        incompatibleField: incompatibleField.displayName,
      }
    );
  }

  get isMovableInLayout(): boolean {
    return true;
  }

  get isDeletableInLayout(): boolean {
    return true;
  }
}

export default UserField;
